/**
 * UsersInvitesTab — Aba «Convites» (Brief 5 A1, W3.1; completada em 04/Set/2026, achado A-01).
 *
 * Antes: o formulário gravava o convite e dizia «enviado» — sem e-mail, sem link, sem aceite; a
 * lista pedia um endpoint que não existia e mostrava um aviso amarelo para sempre.
 *
 * Agora: ao criar (ou reenviar), a tela mostra o LINK de aceite (o segredo só existe nesta
 * resposta) com «Copiar», e diz honestamente o que aconteceu com o e-mail. A lista é real, com
 * «Reenviar» (pendente/expirado) e «Cancelar» (pendente, confirmação em dois cliques — nunca
 * `window.confirm`).
 *
 * Pattern D-B: gate `users-invite` próprio — componente autônomo.
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import {
  useInviteUser,
  useInvites,
  useResendInvite,
  useRevokeInvite,
} from '../../../hooks/use-users';
import { useToastStore } from '../../../stores/toast-store';

import { buildInviteLink, emailDeliveryCopy } from './invite-link';
import { USER_ROLE_LABELS } from './types';

import type { InviteCreatedWire, InviteTokenWireItem } from './types';

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-600',
  revoked: 'bg-red-100 text-red-700',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  accepted: 'Aceito',
  expired: 'Expirado',
  revoked: 'Cancelado',
};

const ROLE_OPTIONS = [
  { value: 'atendente', label: 'Atendente' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'admin_tenant', label: 'Admin' },
  { value: 'dpo_tenant', label: 'DPO' },
];

function formatExpiry(val: string): string {
  return new Date(val).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Painel do link — aparece depois de criar ou reenviar. O segredo não volta a aparecer. */
export function InviteLinkPanel({
  created,
  onClose,
}: {
  created: InviteCreatedWire;
  onClose: () => void;
}): JSX.Element {
  const link = buildInviteLink(window.location.origin, created.acceptSecret);
  const push = useToastStore((s) => s.push);
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      push({ variant: 'success', title: 'Link copiado', durationMs: 2500 });
    } catch {
      push({
        variant: 'error',
        title: 'Não deu para copiar automaticamente',
        description: 'Selecione o link e copie à mão.',
      });
    }
  }

  return (
    <section
      className="rounded-lg border border-border-subtle bg-surface-raised p-4 space-y-2"
      role="status"
      aria-label="Link do convite"
      data-testid="invite-link-panel"
    >
      <p className="text-sm font-medium text-text-primary">
        Convite para <span className="font-mono">{created.email}</span> ·{' '}
        {USER_ROLE_LABELS[created.primaryRole] ?? created.primaryRole}
      </p>
      <p className="text-xs text-text-secondary">
        {emailDeliveryCopy(created.emailDelivery, created.email)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="input flex-1 min-w-64 font-mono text-xs"
          readOnly
          value={link}
          aria-label="Link de aceite do convite"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="btn btn--primary btn--sm" onClick={() => void copy()}>
          {copied ? 'Copiado' : 'Copiar link'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onClose}
          aria-label="Fechar painel do link"
        >
          Fechar
        </button>
      </div>
      <p className="text-xs text-text-secondary">
        O link vale até {formatExpiry(created.expiresAt)} e só aparece agora — se fechar sem copiar,
        use «Reenviar».
      </p>
    </section>
  );
}

export function UsersInvitesTab(): JSX.Element {
  const canInvite = useCapability('users-invite');
  const { data, isLoading, isError } = useInvites();
  const inviteMut = useInviteUser();
  const revokeMut = useRevokeInvite();
  const resendMut = useResendInvite();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('atendente');
  const [formError, setFormError] = useState('');
  const [created, setCreated] = useState<InviteCreatedWire | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  if (!canInvite) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="text-4xl">📧</span>
        <p className="mt-3 text-sm font-medium text-text-secondary">
          Sem permissão para gerenciar convites
        </p>
        <p className="mt-1 text-xs text-text-secondary">
          O envio de convites é restrito a gestores e administradores do tenant.
        </p>
      </div>
    );
  }

  async function handleInvite(): Promise<void> {
    setFormError('');
    if (!email.trim() || !email.includes('@')) {
      setFormError('E-mail inválido.');
      return;
    }
    try {
      const result = await inviteMut.mutateAsync({ email: email.trim(), primaryRole: role });
      setEmail('');
      setRole('atendente');
      setCreated(result);
    } catch (err) {
      const status = (err as { status?: number }).status;
      setFormError(
        status === 409
          ? 'Já existe um usuário com este e-mail neste tenant.'
          : 'Não foi possível criar o convite. Tente de novo.',
      );
    }
  }

  async function handleResend(inv: InviteTokenWireItem): Promise<void> {
    setRowError(null);
    try {
      const result = await resendMut.mutateAsync(inv.id);
      setCreated(result);
    } catch {
      setRowError(`Não foi possível reenviar o convite de ${inv.email}.`);
    }
  }

  async function handleRevoke(inv: InviteTokenWireItem): Promise<void> {
    setRowError(null);
    try {
      await revokeMut.mutateAsync({ inviteId: inv.id });
      setConfirmRevokeId(null);
    } catch {
      setRowError(`Não foi possível cancelar o convite de ${inv.email}.`);
    }
  }

  const invites: InviteTokenWireItem[] = data?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-lg border border-border-subtle p-5">
        <h3 className="mb-4 text-sm font-semibold text-text-primary">Enviar novo convite</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-medium text-text-secondary mb-1">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="novo@empresa.com"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
              aria-label="E-mail do convidado"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Papel inicial
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
              aria-label="Papel do convidado"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => void handleInvite()}
            disabled={inviteMut.isPending || !email.trim()}
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {inviteMut.isPending ? 'Criando…' : 'Criar convite'}
          </button>
        </div>
        {formError && (
          <p className="mt-2 text-xs text-red-600" role="alert">
            {formError}
          </p>
        )}
      </section>

      {created !== null && <InviteLinkPanel created={created} onClose={() => setCreated(null)} />}

      <section>
        <h3 className="mb-3 text-sm font-semibold text-text-primary">Convites enviados</h3>

        {isLoading && <p className="text-sm text-text-secondary">Carregando convites…</p>}

        {isError && (
          <p className="text-sm text-red-600" role="alert">
            Não foi possível carregar os convites. Recarregue a página.
          </p>
        )}

        {rowError !== null && (
          <p className="mb-2 text-xs text-red-600" role="alert">
            {rowError}
          </p>
        )}

        {!isLoading && !isError && invites.length === 0 && (
          <p className="text-sm text-text-secondary">Nenhum convite enviado ainda.</p>
        )}

        {!isLoading && !isError && invites.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border-subtle">
            <table className="min-w-full divide-y divide-border-subtle text-sm">
              <thead className="bg-surface-raised">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-text-secondary">E-mail</th>
                  <th className="px-4 py-3 text-left font-medium text-text-secondary">Papel</th>
                  <th className="px-4 py-3 text-left font-medium text-text-secondary">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-text-secondary">Expira em</th>
                  <th className="px-4 py-3 text-left font-medium text-text-secondary">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle bg-surface">
                {invites.map((inv) => {
                  const canResend = inv.status === 'pending' || inv.status === 'expired';
                  const canRevoke = inv.status === 'pending';
                  const confirming = confirmRevokeId === inv.id;
                  return (
                    <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-text-primary">{inv.email}</td>
                      <td className="px-4 py-3 text-text-secondary">
                        {USER_ROLE_LABELS[inv.primaryRole] ?? inv.primaryRole}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[inv.status] ?? 'bg-gray-100 text-gray-500'}`}
                        >
                          {STATUS_LABEL[inv.status] ?? inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {formatExpiry(inv.expiresAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {canResend && (
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm"
                              disabled={resendMut.isPending}
                              onClick={() => void handleResend(inv)}
                            >
                              Reenviar
                            </button>
                          )}
                          {canRevoke && !confirming && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => setConfirmRevokeId(inv.id)}
                            >
                              Cancelar
                            </button>
                          )}
                          {canRevoke && confirming && (
                            <>
                              <button
                                type="button"
                                className="btn btn--danger btn--sm"
                                disabled={revokeMut.isPending}
                                onClick={() => void handleRevoke(inv)}
                              >
                                Confirmar cancelamento
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setConfirmRevokeId(null)}
                              >
                                Voltar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
